import type { ReactNode } from 'react';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useClickAway, useFirstMountState, useKey } from 'react-use';
import { TextIcon } from '@noths/polaris-client-ribbons-design-system';

import chevronUp from 'src/assets/svg/chevronUp.svg';
import { ANIMATION_DURATION_IN_MS, POPOVER_CONTENT_CLASSNAME } from '../constants';
import * as styles from '../styles';

export interface PopoverButtonProps {
  children: ReactNode;
  buttonMarkup: ReactNode | string;
}

export type ImperativeRef = {
  closeContent: () => void;
};

export const PopoverButton = React.forwardRef<ImperativeRef | null, PopoverButtonProps>(
  ({ children, buttonMarkup }: PopoverButtonProps, iconButtonRef) => {
    const [isContentOpen, setIsContentOpen] = useState(false);
    const [isPopupContentAnimating, setIsPopupContentAnimating] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const openContentButtonRef = useRef<HTMLButtonElement | null>(null);
    const closeContentButtonRef = useRef<HTMLButtonElement | null>(null);
    const openContent = () => setIsContentOpen(true);
    const closeContent = () => setIsContentOpen(false);
    const isFirstMount = useFirstMountState();

    useImperativeHandle(iconButtonRef, () => ({
      closeContent,
    }));

    useClickAway(contentRef, closeContent);
    useKey('Escape', closeContent);

    useEffect(() => {
      if (!isFirstMount) {
        const elementToFocus = isContentOpen ? closeContentButtonRef : openContentButtonRef;
        elementToFocus.current?.focus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isContentOpen]);

    return (
      <div
        data-testid="popover-container"
        data-is-open={isPopupContentAnimating}
        css={styles.popoverButtonWrapper}
      >
        <TextIcon
          data-testid="open-content-button"
          ref={openContentButtonRef}
          icon={chevronUp}
          iconWidth={16}
          iconHeight={16}
          aria-haspopup="true"
          aria-expanded={isContentOpen}
          onClick={openContent}
        >
          {buttonMarkup}
        </TextIcon>
        <CSSTransition
          in={isContentOpen}
          timeout={ANIMATION_DURATION_IN_MS}
          onEnter={() => setIsPopupContentAnimating(true)}
          onExit={() => setIsPopupContentAnimating(false)}
          classNames={POPOVER_CONTENT_CLASSNAME}
          unmountOnExit
        >
          <div
            data-testid="content-wrapper"
            css={styles.popoverButtonContentContainer}
            ref={contentRef}
          >
            <TextIcon
              data-testid="close-content-button"
              ref={closeContentButtonRef}
              icon={chevronUp}
              iconWidth={16}
              iconHeight={16}
              onClick={closeContent}
            >
              {buttonMarkup}
            </TextIcon>
            <div data-testid="content-children-wrapper" role="menu">
              {children}
            </div>
          </div>
        </CSSTransition>
      </div>
    );
  },
);
